// features/projects/templates/professional/widgets/phase-a-coverage.js
// ═══════════════════════════════════════════════════════════
// PHASE A COVERAGE — Tier-A widget
//
// Q: "How adversarially-reviewed is my research, and which
//     specific findings are still unverified?"
//
// 5-layer card: Read (radial dial + per-status counts) · Story
// (what the coverage rate means) · Takeaway (Phase A as
// precision-not-invalidation) · Action (which finding to
// review next) · Caveat (which findings carry elevated caution).
//
// Ported from PhaseACoverageCard in continuum/dashboard.js with
// the same shape adapted to the Lab palette.
// ═══════════════════════════════════════════════════════════

import { L, FONT } from '../tokens';
import { useWidgetData } from './data-provider';
import { DYNAMICS } from '../../../../../lib/continuum/simulation/methodology';
import {
  WidgetShell, LayerSection, LayerAction, CaveatStrip,
} from './findings-status';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const PHASE_A_COLOR = {
  survived:               L.sage,
  'survived-respecified': L.gold,
  killed:                 L.terra,
};
const PHASE_A_LABEL = {
  survived:               'survived',
  'survived-respecified': 'survived · respecified',
  killed:                 'killed',
};

export default function PhaseACoverageWidget() {
  const { loading, comparisons } = useWidgetData();

  if (loading) {
    return (
      <WidgetShell label="Phase A Coverage">
        <div style={{ fontSize: 12, color: L.dim, fontStyle: 'italic', fontFamily: FONT.sans }}>Loading…</div>
      </WidgetShell>
    );
  }

  const substantive = comparisons.filter(c =>
    c.verdict && !['pending', 'insufficient_data', 'error'].includes(String(c.verdict).toLowerCase())
  );
  const covered = substantive.filter(c => c.phase_a_status);
  const uncovered = substantive.filter(c => !c.phase_a_status);

  // Empty state — keep it short, single paragraph. No labeled
  // sections / takeaway / caveat that fill the card with explanation
  // about what isn't there yet.
  if (substantive.length === 0) {
    return (
      <WidgetShell label="Phase A Coverage">
        <div style={{ fontSize: 12, color: L.mid, lineHeight: 1.55, fontFamily: FONT.sans }}>
          No substantive findings yet. Phase A applies once findings have data + verdicts.
        </div>
      </WidgetShell>
    );
  }

  const survivedCount  = covered.filter(c => c.phase_a_status === 'survived').length;
  const respecCount    = covered.filter(c => c.phase_a_status === 'survived-respecified').length;
  const killedCount    = covered.filter(c => c.phase_a_status === 'killed').length;

  const coveragePct = substantive.length === 0 ? 0
    : Math.round((covered.length / substantive.length) * 100);

  const story = composeStory(covered, uncovered, substantive);
  const takeaway = composeTakeaway(survivedCount, respecCount, killedCount, covered);
  const action = composeAction(uncovered);
  const caveat = composeCaveat(uncovered);

  return (
    <WidgetShell label="Phase A Coverage">
      {/* READ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10 }}>
        <RadialDial
          pct={coveragePct}
          color={coveragePct >= 70 ? L.sage : coveragePct >= 30 ? L.gold : L.terra}
        />
        <div>
          <div style={{
            fontFamily: FONT.mono, fontSize: 24, color: L.text,
            fontWeight: 300, letterSpacing: '-0.01em',
          }}>
            {covered.length}<span style={{ fontSize: 14, color: L.dim }}> / {substantive.length}</span>
          </div>
          <div style={{ fontSize: 11, color: L.mid, fontFamily: FONT.sans, marginTop: 2 }}>
            substantive findings reviewed
          </div>
        </div>
      </div>

      {covered.length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 12,
          fontFamily: FONT.mono, fontSize: 10, marginTop: 6,
        }}>
          {survivedCount > 0 && <Stat color={PHASE_A_COLOR.survived} count={survivedCount} label={PHASE_A_LABEL.survived} />}
          {respecCount > 0 && <Stat color={PHASE_A_COLOR['survived-respecified']} count={respecCount} label={PHASE_A_LABEL['survived-respecified']} />}
          {killedCount > 0 && <Stat color={PHASE_A_COLOR.killed} count={killedCount} label={PHASE_A_LABEL.killed} />}
        </div>
      )}

      <LayerSection label="Story">{story}</LayerSection>
      <LayerSection label="Takeaway" accent={L.gold}>{takeaway}</LayerSection>
      {action && (
        <LayerAction accent={L.sage} label={action.label} why={action.why} />
      )}
      {caveat && <CaveatStrip text={caveat} />}
    </WidgetShell>
  );
}

function Stat({ color, count, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      color, letterSpacing: '0.04em',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
      {count} {label}
    </span>
  );
}

function RadialDial({ pct, color, size = 56 }) {
  const r = (size / 2) - 4;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const rest = circ - dash;
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={L.b1} strokeWidth="3" />
      <circle
        cx={cx} cy={cy} r={r}
        fill="none" stroke={color} strokeWidth="3"
        strokeDasharray={`${dash} ${rest}`}
        strokeDashoffset={circ / 4}
        transform={`rotate(-90 ${cx} ${cy})`}
        strokeLinecap="round"
      />
      <text
        x={cx} y={cy + 3} textAnchor="middle"
        fontFamily="DM Mono, monospace" fontSize="11"
        fill={color} fontWeight="600"
      >
        {pct}%
      </text>
    </svg>
  );
}

function composeStory(covered, uncovered, substantive) {
  if (substantive.length === 0) return 'No substantive findings yet — Phase A applies once findings have data + verdicts.';
  if (uncovered.length === 0) return `All ${substantive.length} substantive findings have been adversarially reviewed.`;
  return `${covered.length} of ${substantive.length} substantive findings have been Phase A red-teamed. The remaining ${uncovered.length} carry elevated interpretive caution until reviewed.`;
}

function composeTakeaway(survivedCount, respecCount, killedCount, covered) {
  if (covered.length === 0) {
    return 'Phase A is the methodology\'s adversarial review step — running it converts an unverified claim into a defensible one (or sharpens it via respecification).';
  }
  if (respecCount > 0 && killedCount === 0) {
    return `${respecCount} of ${covered.length} reviewed claim${covered.length === 1 ? '' : 's'} survived only after being respecified — Phase A is producing precision, not invalidation. Treat respecified versions as the load-bearing claims.`;
  }
  if (killedCount > 0) {
    return `${killedCount} claim${killedCount === 1 ? '' : 's'} killed by Phase A. Treat as evidence against, not as null results — the methodology actively refuted them.`;
  }
  return `All ${covered.length} reviewed claim${covered.length === 1 ? '' : 's'} survived intact. Defensible to cite externally with the standard caveats.`;
}

function composeAction(uncovered) {
  if (uncovered.length === 0) return null;
  const target = uncovered.slice().sort((a, b) =>
    Math.abs(Number(b.effect_size) || 0) - Math.abs(Number(a.effect_size) || 0))[0];
  const dynLabel = DYN_BY_KEY[target.dynamic_key]?.label || target.dynamic_key;
  return {
    label: `Run Phase A on ${dynLabel}`,
    why: `Largest unreviewed effect (d=${(Number(target.effect_size) || 0).toFixed(2)}). ~30s + ~$0.20 per run.`,
  };
}

function composeCaveat(uncovered) {
  if (uncovered.length === 0) return null;
  const names = uncovered.map(c => DYN_BY_KEY[c.dynamic_key]?.label || c.dynamic_key);
  return `Not yet red-teamed: ${names.join(' · ')}. These claims should be discounted in any downstream artifact (paper outline, donor brief, talk) until run.`;
}
